// src/components/OnboardingForm.tsx

import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase/config';
import { collection, addDoc, doc, updateDoc, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import { sendOnboardingFormNotification, sendGLBAPrivacyNotice } from '../lib/email/emailService';
import { markInitialNoticeDelivered } from '../lib/glba/glbaPreferencesApi';

const OnboardingForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    businessName: '',
    businessType: '',
    ein: '',
    selectedServices: [] as string[],
    files: [] as File[],
    comments: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Load existing client data on mount if available
  useEffect(() => {
    const loadExistingClientData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setIsLoading(false);
          return;
        }

        // Set email from authenticated user if available
        if (user.email) {
          setFormData(prev => ({ ...prev, email: user.email || '' }));
        } else {
          setIsLoading(false);
          return;
        }

        // Check if client document already exists for this user (by email or userId)
        const clientsRef = collection(db, 'clients');
        const emailQuery = query(clientsRef, where('email', '==', user.email));
        const userIdQuery = query(clientsRef, where('userId', '==', user.uid));
        
        const [emailSnapshot, userIdSnapshot] = await Promise.all([
          getDocs(emailQuery),
          getDocs(userIdQuery)
        ]);

        // Find existing client by email or userId
        let existingClient: { id: string; firstName?: string; lastName?: string; email?: string; businessName?: string; businessType?: string; ein?: string; selectedServices?: string[]; comments?: string } | null = null;
        emailSnapshot.forEach((doc) => {
          existingClient = { id: doc.id, ...doc.data() };
        });
        
        // If not found by email, check by userId
        if (!existingClient) {
          userIdSnapshot.forEach((doc) => {
            existingClient = { id: doc.id, ...doc.data() };
          });
        }

        // If existing client found, pre-populate the form
        if (existingClient) {
          setFormData(prev => ({
            ...prev,
            firstName: existingClient?.firstName || '',
            lastName: existingClient?.lastName || '',
            email: existingClient?.email || user.email || '',
            businessName: existingClient?.businessName || '',
            businessType: existingClient?.businessType || '',
            ein: existingClient?.ein || '',
            selectedServices: existingClient?.selectedServices || [],
            comments: existingClient?.comments || '',
          }));
        }
      } catch (error) {
        console.error('Error loading existing client data:', error);
        // Don't block the form if there's an error loading existing data
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingClientData();
  }, []);

  const services = [
    'Bookkeeping', 
    'Payroll', 
    'Contracts', 
    'HR',
    'Risk Management', 
    'Grant Management', 
    'Facilities',
    'For-profit entity formation',
    'Non-profit entity formation',
    'Fractional CFO services',
    'Trust creation',
    'Power of Attorney (POA) - Assistance',
    'Annual reporting filing'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({ ...prev, files: Array.from(e.target.files!) }));
    }
  };

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(service)
        ? prev.selectedServices.filter(s => s !== service)
        : [...prev.selectedServices, service]
    }));
  };

  // Upload files to Bluehost server
  const uploadFilesToServer = async (files: File[], userId: string): Promise<string[]> => {
    const uploadedFileUrls: string[] = [];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('clientId', userId); // Use authenticated user's ID
      
      try {
        const response = await fetch('https://helpkeepmymoney.com/upload.php', {
          method: 'POST',
          body: formData,
        });
        
        if (response.ok) {
          const result = await response.json();
          uploadedFileUrls.push(result.fileUrl);
        } else {
          throw new Error(`Upload failed for ${file.name}`);
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        throw error;
      }
    }
    
    return uploadedFileUrls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Save client data to Firebase Firestore
      const user = auth.currentUser;
      
      if (!user) {
        setErrorMessage('You must be signed in to complete onboarding.');
        setIsSubmitting(false);
        return;
      }

      // Upload files to Bluehost server first
      let uploadedFileUrls: string[] = [];
      if (formData.files.length > 0) {
        try {
          uploadedFileUrls = await uploadFilesToServer(formData.files, user.uid);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          setErrorMessage('File upload failed. Please try again or contact support.');
          setIsSubmitting(false);
          return;
        }
      }

      // Validate that the form email matches the authenticated user's email
      const formEmail = formData.email.trim().toLowerCase();
      const userEmail = (user.email || '').trim().toLowerCase();
      
      if (formEmail !== userEmail) {
        setErrorMessage('The email address must match your registered account email address.');
        setIsSubmitting(false);
        return;
      }

      // Combine firstName and lastName for displayName
      const displayName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();

      // Update user document with firstName, lastName, and displayName
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        displayName: displayName, // Store combined name for backwards compatibility
        updatedAt: new Date()
      });

      // Check if client document already exists (by email or userId)
      const clientsRef = collection(db, 'clients');
      const emailQuery = query(clientsRef, where('email', '==', formEmail));
      const userIdQuery = query(clientsRef, where('userId', '==', user.uid));
      
      const [emailSnapshot, userIdSnapshot] = await Promise.all([
        getDocs(emailQuery),
        getDocs(userIdQuery)
      ]);

      // Find existing client document
      let existingClientId: string | null = null;
      let existingUploadedFiles: string[] = [];
      
      // Check by email first
      if (!emailSnapshot.empty) {
        emailSnapshot.forEach((doc) => {
          existingClientId = doc.id;
          const data = doc.data();
          existingUploadedFiles = data.uploadedFiles || [];
        });
      }
      
      // If not found by email, check by userId
      if (!existingClientId && !userIdSnapshot.empty) {
        userIdSnapshot.forEach((doc) => {
          existingClientId = doc.id;
          const data = doc.data();
          existingUploadedFiles = data.uploadedFiles || [];
        });
      }

      // Merge new files with existing files
      const allUploadedFiles = [...existingUploadedFiles, ...uploadedFileUrls];

      const isUpdate = existingClientId !== null;
      
      console.log('Onboarding form submission detection:', {
        isUpdate,
        existingClientId,
        emailQueryResults: emailSnapshot.size,
        userIdQueryResults: userIdSnapshot.size,
      });

      if (existingClientId) {
        // Update existing client document
        const clientDocRef = doc(db, 'clients', existingClientId);
        await updateDoc(clientDocRef, {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          fullName: displayName,
          email: formEmail,
          businessName: formData.businessName.trim(),
          businessType: formData.businessType.trim(),
          ein: formData.ein.trim(),
          selectedServices: formData.selectedServices,
          comments: formData.comments.trim(),
          uploadedFiles: allUploadedFiles,
          userId: user.uid, // Ensure userId is always set correctly
          updatedAt: new Date()
        });
      } else {
        // Create new client document
        await addDoc(collection(db, 'clients'), {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          fullName: displayName,
          email: formEmail,
          businessName: formData.businessName.trim(),
          businessType: formData.businessType.trim(),
          ein: formData.ein.trim(),
          selectedServices: formData.selectedServices,
          comments: formData.comments.trim(),
          uploadedFiles: allUploadedFiles,
          userId: user.uid,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      // Send notification email (don't wait for it to complete)
      console.log('Sending onboarding form notification:', {
        isUpdate,
        existingClientId,
        clientEmail: formEmail,
        clientName: displayName,
      });
      
      sendOnboardingFormNotification(
        formEmail,
        displayName,
        formData.businessName.trim(),
        formData.businessType.trim(),
        formData.ein.trim(),
        formData.selectedServices,
        isUpdate
      ).catch((err) => {
        console.error('Failed to send onboarding form notification email:', err);
      });

      // Send initial GLBA Financial Privacy Notice if this is a new client
      if (!existingClientId) {
        try {
          await sendGLBAPrivacyNotice(formEmail, 'initial');
          await markInitialNoticeDelivered(user.uid);
        } catch (err) {
          console.error('Failed to send GLBA privacy notice:', err);
          // Don't block form submission if notice fails
        }
      }

      setSubmitStatus('success');
      
      // Navigate to client dashboard after a short delay
      setTimeout(() => {
        navigate('/client-dashboard');
      }, 2000);

    } catch (error) {
      console.error('Onboarding failed:', error);
      setSubmitStatus('error');
      
      // Provide helpful error messages
      if (error instanceof Error) {
        if (error.message.includes('permission-denied')) {
          setErrorMessage('Firebase security rules are blocking access. Please check your Firebase project setup.');
        } else if (error.message.includes('unavailable')) {
          setErrorMessage('Firebase service is unavailable. Please check your internet connection and try again.');
        } else {
          setErrorMessage(`Submission failed: ${error.message}`);
        }
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <Navigation />
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
          <p>Loading your information...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }}>
        <form onSubmit={handleSubmit}>
        <h2>Client Onboarding</h2>

        {submitStatus === 'success' && (
          <div style={{ 
            backgroundColor: '#d4edda', 
            color: '#155724', 
            padding: '10px', 
            borderRadius: '4px', 
            marginBottom: '20px' 
          }}>
            ✅ Form submitted successfully! Redirecting to dashboard...
          </div>
        )}

        {submitStatus === 'error' && (
          <div style={{ 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            padding: '10px', 
            borderRadius: '4px', 
            marginBottom: '20px' 
          }}>
            ❌ {errorMessage}
          </div>
        )}

        <div style={{ marginBottom: '15px' }}>
          <label>First Name:</label>
          <input 
            type="text" 
            name="firstName" 
            value={formData.firstName}
            onChange={handleChange} 
            required 
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Last Name:</label>
          <input 
            type="text" 
            name="lastName" 
            value={formData.lastName}
            onChange={handleChange} 
            required 
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Email:</label>
          <input 
            type="email" 
            name="email" 
            value={formData.email}
            onChange={handleChange} 
            required 
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
            Must match your registered account email address
          </small>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Primary Business Name:</label>
          <input 
            type="text" 
            name="businessName" 
            value={formData.businessName}
            onChange={handleChange} 
            required 
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Primary Business Type:</label>
          <input 
            type="text" 
            name="businessType" 
            value={formData.businessType}
            onChange={handleChange} 
            required 
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Primary EIN:</label>
          <input 
            type="text" 
            name="ein" 
            value={formData.ein}
            onChange={handleChange} 
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Comments:</label>
          <textarea 
            name="comments" 
            value={formData.comments}
            onChange={handleChange}
            rows={4}
            style={{ width: '100%', padding: '8px', marginTop: '5px', fontFamily: 'inherit', resize: 'vertical' }}
            placeholder="Any additional comments or notes..."
          />
        </div>

        <fieldset style={{ marginBottom: '15px', padding: '15px' }}>
          <legend>Select Services:</legend>
          {services.map(service => (
            <label key={service} style={{ display: 'block', marginBottom: '8px' }}>
              <input
                type="checkbox"
                checked={formData.selectedServices.includes(service)}
                onChange={() => handleServiceToggle(service)}
                style={{ marginRight: '8px' }}
              />
              {service}
            </label>
          ))}
        </fieldset>

        <div style={{ marginBottom: '15px' }}>
          <label>Upload Documents:</label>
          <input 
            type="file" 
            multiple 
            onChange={handleFileChange} 
            style={{ width: '100%', padding: '8px', marginTop: '5px' }}
          />
          <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
            Files will be uploaded to our secure server
          </small>
        </div>

        {/* Notice at Collection */}
        <div style={{
          marginBottom: '20px',
          padding: '12px',
          backgroundColor: '#e7f3ff',
          border: '1px solid #007bff',
          borderRadius: '4px',
          fontSize: '0.875rem'
        }}>
          <strong>Notice at Collection (CCPA/CPRA):</strong>
          <p style={{ margin: '8px 0 0 0', lineHeight: '1.5' }}>
            By submitting this form, you acknowledge that we will collect and use the following categories 
            of personal information: <strong>Identifiers</strong> (name, email, phone, address), 
            <strong> Financial Information</strong> (EIN, business financial data), 
            <strong> Commercial Information</strong> (business details, services selected), and 
            <strong> Documents</strong> (uploaded files and attachments). We collect this information to 
            provide onboarding services, manage your account, and fulfill your service requests. 
            For more details, see our{' '}
            <a href="/privacy-policy" style={{ color: '#007bff', textDecoration: 'none' }}>
              Privacy Policy
            </a>.
          </p>
        </div>

        <button 
          type="submit" 
          disabled={isSubmitting}
          style={{ 
            backgroundColor: isSubmitting ? '#ccc' : '#007bff', 
            color: 'white', 
            padding: '10px 20px', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            width: '100%'
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
      </div>
    </div>
  );
};

export default OnboardingForm;
